import { relations } from "drizzle-orm/_relations";
import {
  type AnyPgColumn,
  boolean,
  index,
  integer,
  pgTable,
  serial,
  text,
  unique,
  uuid,
} from "drizzle-orm/pg-core";
import { createSchemaFactory } from "drizzle-zod";
import { z } from "zod";

import { users } from "./auth";
import { playoffBracketTemplates } from "./playoff-bracket-templates";
import { richText, venueStatusEnum } from "./shared";
import { tournaments } from "./tournaments";
import { venueDirectors } from "./venue-directors";

const { createInsertSchema, createSelectSchema, createUpdateSchema } =
  createSchemaFactory({ zodInstance: z });

export const venues = pgTable(
  "venues",
  {
    id: serial().primaryKey(),
    slug: text().notNull().unique(),
    name: text().notNull(),
    city: text().notNull(),
    description: richText().notNull(),
    directions: richText().notNull(),
    mapUrl: text().notNull(),
    status: venueStatusEnum().notNull(),
    imageSource: text(),
    headerImageSource: text(),
    thumbnailImageSource: text(),
    // TODO: add check for confirming user has director role
    directorId: text().references(() => users.id),
    externalRef: uuid().unique(),
    order: integer().default(0).notNull(),
    withdrawDeadlineHours: integer().notNull().default(21),
    defaultAutopromoteWaitlist: boolean().notNull().default(true),
    registrationOpenDaysBefore: integer(),
    // Circular FK (venues ↔ templates) — the explicit AnyPgColumn return type
    // breaks the type-inference cycle that would otherwise degrade these tables.
    defaultBracketTemplateId: integer().references(
      (): AnyPgColumn => playoffBracketTemplates.id,
      { onDelete: "set null" },
    ),
  },
  (table) => [
    unique("name_city_unique").on(table.name, table.city),
    index("external_ref_idx").on(table.externalRef),
    index("venues_director_id_idx").on(table.directorId),
    index("venues_status_order_name_idx").on(
      table.status,
      table.order,
      table.name,
    ),
  ],
);

export const selectVenueSchema = createSelectSchema(venues);
export const createVenueSchema = createInsertSchema(venues).omit({
  id: true,
});
export const updateVenueSchema = createUpdateSchema(venues).pick({
  description: true,
  directions: true,
  headerImageSource: true,
  thumbnailImageSource: true,
  withdrawDeadlineHours: true,
});

export type Venue = z.infer<typeof selectVenueSchema>;
export type CreateVenue = z.infer<typeof createVenueSchema>;
export type UpdateVenue = z.infer<typeof updateVenueSchema>;

export const venuesRelations = relations(venues, ({ one, many }) => ({
  director: one(users, {
    fields: [venues.directorId],
    references: [users.id],
  }),
  directors: many(venueDirectors),
  tournaments: many(tournaments),
}));
